import React from "react";
import { useState, useEffect, useContext } from "react";
import {
  Card,
  Typography,
  Grid2,
  CircularProgress,
  TextField,
  FormHelperText,
  Button,
  FormControl,
  Checkbox,
  ListItemText,
  Tooltip,
  Box,
} from "@mui/material";
import dayjs from "dayjs";
import { getServiceDetails } from "../API/LinkedServicesApi";
import { uploadMedicalRecord } from "../API/MedicalRecordApi";
import {
  getMedicalRecordMetadata,
  downloadMedicalRecord,
} from "../API/MedicalRecordApi"; // Added imported APIs
import AuthContext from "../Context/AuthProvider";
import { useSnackbar } from "../Utility/UserMessageContext";
import { CloudUpload, Close, Visibility, Delete } from "@mui/icons-material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import { OutlinedInput, InputLabel, Select, MenuItem } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  recordTypeOptions,
  formatDateInWords,
  formatDateToWords,
} from "../Utility/Constants";
import { formatAge, showLoadingImage } from "../Utility/Utility";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import { validateVideoAndCompress } from "../Utility/VideoFileUtility";
import MenuBar from "../common/MenuBar";
import "../common/styles/CommonStyles.css"; // Import your custom styles
import GenericTextField from "../common/GenericTextField";
import HeaderValueDisplay from "../common/HeaderValueDisplay";
import GenericDatePicker from "../common/GenericDatePicker";
import GenericDropDown from "../common/GenericDropDown";
import GenericFileUploader from "../common/GenericFileUploader";

function MedicalRecord({ availedServiceId }) {
  const [currentService, setCurrentService] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [medicalRecordName, setMedicalRecordName] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { auth } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const [medicalRecordDate, setMedicalRecordDate] = useState("");
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [description, setDescription] = useState(""); // Renamed from notes to description
  const theme = useTheme();
  const [recordType, setRecordType] = useState("");
  const [medicalRecordType, setMedicalRecordType] = useState("");
  const [fileName, setFileName] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);
  const [formattedMedicalRecordDate, setFormattedMedicalRecordDate] =
    useState("");
  const [fileSizeError, setFileSizeError] = useState(false); // New state for file size error
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 10MB in bytes
  const [videoDurationError, setVideoDurationError] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  // New state for metadata
  const [medicalRecordMetadata, setMedicalRecordMetadata] = useState([]);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [downloadingRecord, setDownloadingRecord] = useState(false);

  const isVideo = selectedFile && selectedFile.type.startsWith("video/");

  // Maximum character count for description
  const MAX_DESCRIPTION_LENGTH = 2000;

  const handleDateChange = (e) => {
    setMedicalRecordDate(e.target.value);
  };

  const handleDescriptionChange = (event) => {
    // Only update if within character limit
    if (event.target.value.length <= MAX_DESCRIPTION_LENGTH) {
      setDescription(event.target.value);
    }
  };

  // Validate medical record name (alphanumeric, spaces, dots, hyphens only)
  const validateMedicalRecordName = (name) => {
    const regex = /^[a-zA-Z0-9\s.\-]+$/;
    return regex.test(name);
  };

  // Handle medical record name change with validation
  const handleMedicalRecordNameChange = (e) => {
    const value = e.target.value;
    if (value === "" || validateMedicalRecordName(value)) {
      setMedicalRecordName(value);
    }
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  function getStyles(option, selectedOptions, theme) {
    return {
      fontWeight: selectedOptions.includes(option)
        ? theme.typography.fontWeightMedium
        : theme.typography.fontWeightRegular,
    };
  }

  const createMobileCompatibleVideoUrl = (file) => {
  // For mobile devices, sometimes we need to recreate the blob URL
  if (file && file.type.startsWith('video/')) {
    // Clean up old URL first
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    
    // Create new blob URL
    return URL.createObjectURL(file);
  }
  return null;
};

// Add this useEffect to handle preview URL cleanup and mobile optimization
useEffect(() => {
  return () => {
    // Cleanup blob URLs when component unmounts
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  };
}, [previewUrl]);

// Add this useEffect to handle mobile-specific video preview issues
useEffect(() => {
  if (selectedFile && selectedFile.type.startsWith('video/') && !previewUrl) {
    // If we have a video file but no preview URL, create one
    const videoUrl = createMobileCompatibleVideoUrl(selectedFile);
    if (videoUrl) {
      setPreviewUrl(videoUrl);
    }
  }
}, [selectedFile, previewUrl]);

  // Fetch medical record metadata
  const fetchMedicalRecordMetadata = async () => {
    if (auth != null && availedServiceId != null) {
      setLoadingMetadata(true);
      try {
        const result = await getMedicalRecordMetadata(auth, availedServiceId);
        if (result?.isSuccess) {
          setMedicalRecordMetadata(result.data);
          // Extract all existing record names for validation
          const names = result.data.map((record) => record.name.toLowerCase());
        } else {
          showSnackbar(
            result?.errorMessage || "Failed to load medical record metadata",
            "error"
          );
        }
      } catch (error) {
        showSnackbar(
          "Error loading medical record metadata: " + error.message,
          "error"
        );
      } finally {
        setLoadingMetadata(false);
      }
    }
  };

  // Handle medical record download
  const handleDownloadRecord = async (recordId) => {
    if (!recordId) return;

    setDownloadingRecord(true);
    try {
      const result = await downloadMedicalRecord(auth, recordId);
      if (result?.isSuccess) {
        // Create blob URL and trigger download
        const blob = new Blob([result.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        // Use filename from response or fallback to a default
        link.download = result.fileName || `medical-record-${recordId}.pdf`;
        document.body.appendChild(link);
        link.click();

        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);

        showSnackbar("Medical record downloaded successfully", "success");
      } else {
        showSnackbar(
          result?.errorMessage || "Failed to download medical record",
          "error"
        );
      }
    } catch (error) {
      showSnackbar(
        "Error downloading medical record: " + error.message,
        "error"
      );
    } finally {
      setDownloadingRecord(false);
    }
  };

  useEffect(() => {
    const loadService = async () => {
      if (auth != null && availedServiceId != null) {
        setIsLoading(true);
        try {
          const result = await getServiceDetails(auth, availedServiceId);
          if (result?.isSuccess) {
            setCurrentService(result.data);
          } else {
            showSnackbar(
              result?.data || "Failed to load service details",
              "error"
            );
          }
        } catch (error) {
          showSnackbar(
            "Error loading service details: " + error.message,
            "error"
          );
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadService();
    fetchMedicalRecordMetadata(); // Fetch medical record metadata on component mount
  }, [auth, availedServiceId, showSnackbar]);

  // Refresh metadata after successful upload
  const refreshMetadata = () => {
    fetchMedicalRecordMetadata();
  };

  const handleSave = async () => {
    setIsSubmitted(true);

    // Validate required fields
    if (!medicalRecordName.trim()) {
      showSnackbar("Medical Record Name is required", "error");
      return false;
    }

    if (!medicalRecordDate) {
      showSnackbar("Medical Record Date is required", "error");
      return false;
    }

    if (!recordType) {
      showSnackbar("Record Type is required", "error");
      return false;
    }

    if (!selectedFile) {
      showSnackbar("Please select a file to upload", "error");
      return false;
    }

    // Check file size again before uploading
    if (selectedFile.size > MAX_FILE_SIZE) {
      showSnackbar("File size exceeds the maximum limit of 10MB", "error");
      setFileSizeError(true);
      return false;
    }
    if (
      selectedFile &&
      selectedFile.type.startsWith("video/") &&
      videoDurationError
    ) {
      showSnackbar(
        "Video duration exceeds the maximum limit of 60 seconds",
        "error"
      );
      return false;
    }

    try {
      setShowSpinner(true);

      // Call the uploadMedicalRecord API function
      const result = await uploadMedicalRecord(
        auth,
        availedServiceId,
        medicalRecordName,
        medicalRecordDate,
        recordType,
        selectedFile,
        description // Pass description instead of notes
      );

      if (result.isSuccess) {
        showSnackbar("Medical record uploaded successfully", "success");

        // Optional: Reset form after successful upload
        setMedicalRecordName("");
        setMedicalRecordDate("");
        setRecordType("");
        setSelectedFile(null);
        setFileName("");
        setPreviewUrl("");
        setDescription("");
        setIsSubmitted(false);
        setFileSizeError(false);

        // Refresh metadata after successful upload
        refreshMetadata();

        // Refresh metadata after successful upload
        refreshMetadata();

        return true;
      } else {
        showSnackbar(
          result.errorMessage || "Failed to upload medical record",
          "error"
        );
        return false;
      }
    } catch (error) {
      showSnackbar("Error uploading medical record: " + error.message, "error");
      return false;
    } finally {
      setShowSpinner(false); // Set isSaving back to false after upload completes
    }
  };

  // Fixed handleFileChange function
const handleFileChange = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Reset previous states
  setFileSizeError(false);
  setVideoDurationError(false);
  setCompressionProgress(0);

  // Check if file type is allowed
  const isValidFileType =
    file.type === "application/pdf" ||
    file.type.startsWith("image/") ||
    file.type.startsWith("video/");

  if (!isValidFileType) {
    showSnackbar("Only PDF, image, and video files are allowed", "error");
    return;
  }

  // Set loading state only once
  setShowSpinner(true);

  try {
    // For videos, validate and compress if needed
    if (file.type.startsWith("video/")) {
      await handleVideoFileChange(file);
    } else {
      // For PDF and images, check file size
      if (file.size > MAX_FILE_SIZE) {
        showSnackbar("File size exceeds the maximum limit of 10MB", "error");
        setFileSizeError(true);
      }

      // Set file regardless of size for user feedback
      setSelectedFile(file);
      setFileName(file.name);
      const fileUrl = URL.createObjectURL(file);
      setPreviewUrl(fileUrl);
    }
  } catch (error) {
    console.error("Error handling file change:", error);
    showSnackbar("Error processing file", "error");
  } finally {
    // Only turn off spinner here, not in handleVideoFileChange
    setShowSpinner(false);
  }
};

// Replace your handleVideoFileChange function with this fixed version
const handleVideoFileChange = async (file) => {
  try {
    // Validate video file and compress if necessary
    const response = await validateVideoAndCompress(file, setCompressionProgress);
    
    if (!response?.isValid) {
      showSnackbar(response?.errorMessage || "Invalid video file", "error");
      if (response?.errorMessage?.includes("duration")) {
        setVideoDurationError(true);
      }
      // Still set the file for user to see the error
      setSelectedFile(file);
      setFileName(file.name);
      const fileUrl = URL.createObjectURL(file);
      setPreviewUrl(fileUrl);
      return;
    }

    // Use the processed file (either original or compressed)
    const processedFile = response.outputFile || file;
    
    // Check final file size
    if (processedFile.size > MAX_FILE_SIZE) {
      showSnackbar("File size exceeds the maximum limit of 10MB", "error");
      setFileSizeError(true);
    }

    setSelectedFile(processedFile);
    setFileName(processedFile.fileName || processedFile.name || file.name);
    
    // Create preview URL with the processed file
    const fileUrl = URL.createObjectURL(processedFile);
    setPreviewUrl(fileUrl);
    
  } catch (error) {
    console.error("Error in handleVideoFileChange:", error);
    showSnackbar("Error processing video file", "error");
    
    // Fallback to original file
    setSelectedFile(file);
    setFileName(file.name);
    const fileUrl = URL.createObjectURL(file);
    setPreviewUrl(fileUrl);
  }
  // DO NOT set setShowSpinner(false) here - let handleFileChange handle it
};
  // 3. Updated compressVideo function with audio preservation


  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileName("");
    setPreviewUrl("");
    setFileSizeError(false);
    setVideoDurationError(false);
  };

  const handlePreviewOpen = () => {
    setOpenPreview(true);
  };

  const handlePreviewClose = () => {
    setOpenPreview(false);
  };

  const isImage = selectedFile && selectedFile.type.startsWith("image/");
  const isPdf = selectedFile && selectedFile.type === "application/pdf";

  const displayText = (value, fallback = "Not available") => {
    return value !== undefined && value !== null ? value : fallback;
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + " KB";
    else return (bytes / 1048576).toFixed(2) + " MB";
  };

  // Single variable approach: show loading state when isLoading or isSaving is true
  const showLoading = isLoading || showSpinner;

  


return (
    <Grid2 size={12}  sx={{ position: "relative"}}>
      {showSpinner && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(216, 214, 214, 0.4)", // Consistent lighter background
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1301,
          }}
        >
          {compressionProgress > 0 ? (
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: 2,
                boxShadow: 3,
                p: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: 300,
              }}
            >
              <CircularProgress
                variant="determinate"
                value={compressionProgress}
                size={80}
                thickness={4}
                sx={{ 
                  mb: 2,
                  color: "primary.dark", // Or use a specific dark color like "#1976d2"
                }}
              />
              <Typography variant="body1" sx={{ mb: 1 }}>
                Compressing Video...
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }} color="red">
                Do not refresh, close or press back button 
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {compressionProgress}% Complete
              </Typography>
            </Box>
          ) : (
            showLoadingImage()
          )}
        </div>
      )}

      <Grid2 size={12} container>
        <Grid2 size={12}>
          <MenuBar
            showSave={true}
            onSave={handleSave} />            
        </Grid2>

        <Grid2 size={12} container spacing={1} sx={{
              overflow: "auto", // Enable scrolling when content overflows
              maxHeight: "calc(100vh - 180px)", overflowY: "auto"
        }}>
          <Grid2
            size={{ xs: 12, sm: 12, md: 7, lg: 7, xl: 7 }}
            sx={{ paddingTop: "10px"
             }}
          >
            <Card className="outerCard" variant="outlined" sx={{ width: "100%", overflow: "auto", ml: "10px", padding:"10px" }}>
              {/* Patient Details Card - Existing Code */}
              <Grid2 size={12}>
                <Card
                  sx={{
                    padding: "16px",
                    pointerEvents: showLoading ? "none" : "auto",
                    opacity: showLoading ? 0.6 : 1,
                  }}
                  variant="outlined"
                >
                  <Grid2 size={12}>
                    <Grid2 container size={12}>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Patient Name"
                          value={
                            currentService?.patientPrefix +
                            " " +
                            currentService?.patientFirstName +
                            " " +
                            currentService?.patientLastName
                          }
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Age and Gender"
                          value={
                            formatAge(currentService?.patientAgeInYears) +
                            " and " +
                            currentService?.patientGender
                          }
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="MRN"
                          value={displayText(currentService?.patientMRN)}
                        />
                      </Grid2>
                    </Grid2>
                    <Grid2 container size={12} >
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Visit Id"
                          value={displayText(currentService?.visitDisplayId)}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Visit Type"
                          value={displayText(currentService?.visitType)}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Visit Date Time"
                          value={convertUTCToTimeZoneDateTimeString(
                            new Date().toISOString(),
                            Intl.DateTimeFormat().resolvedOptions().timeZone
                          )}
                        />
                      </Grid2>
                    </Grid2>
                    <Grid2 container size={12} >
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Service Id"
                          value={displayText(currentService?.serviceId)}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Service Name"
                          value={displayText(currentService?.serviceName)}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Service Date Time"
                          value={convertUTCToTimeZoneDateTimeString(
                            new Date().toISOString(),
                            Intl.DateTimeFormat().resolvedOptions().timeZone
                          )}
                        />
                      </Grid2>
                    </Grid2>
                  </Grid2>
                </Card>

                <Grid2
                  size={12}
                  container
                  spacing={2}
                  sx={{ marginTop: "10px" }}
                >
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
                    <GenericTextField
                      label="Medical Record Name"
                      value={medicalRecordName || ""}
                      onChange={handleMedicalRecordNameChange}
                      required
                      error={
                        isSubmitted &&
                        (!medicalRecordName.trim() ||
                          !validateMedicalRecordName(medicalRecordName))
                      }
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
                    <GenericDatePicker
                      label="Medical Record Date"
                      value={medicalRecordDate ? dayjs(medicalRecordDate) : null}
                      onChange={(newDate) =>
                        setMedicalRecordDate(
                          newDate ? newDate.format("DD-MMM-YYYY") : ""
                        )
                      }
                      required
                      error={isSubmitted && !medicalRecordDate}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
                    <GenericDropDown
                      label="Record Type"
                      value={recordType}
                      onChange={(e) => setRecordType(e.target.value)}
                      options={recordTypeOptions}
                      required
                      error={isSubmitted && !recordType}
                    />
                  </Grid2>
                  <Grid2 size={12} sx={{ mt: 2 }}>
                    <GenericFileUploader
                      label="Upload Medical Record File"
                      fileName={fileName}
                      selectedFile={selectedFile}
                      onFileChange={handleFileChange}
                      onPreview={handlePreviewOpen}
                      onRemove={handleRemoveFile}
                      error={isSubmitted && (!selectedFile || fileSizeError)}
                      helperText={
                        isSubmitted && !selectedFile
                          ? "Please select a file to upload"
                          : fileSizeError
                          ? "File size exceeds the maximum limit of 10MB"
                          : videoDurationError
                          ? "Video duration exceeds the maximum limit of 60 seconds"
                          : ""
                      }
                      formatFileSize={formatFileSize}
                    />
                    {/* <Typography>
                      Upload Medical Record File ( max 10MB)
                    </Typography> */}

                    {/* <Grid2 container size={12} spacing={1} alignItems="center">
                      <Grid2 size={{ xs: 12, sm: 7, md: 7, lg: 11, xl: 11 }}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          placeholder="No file chosen"
                          value={fileName}
                          InputProps={{
                            readOnly: true,
                            endAdornment: selectedFile && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={handlePreviewOpen}
                                  disabled={!isImage && !isPdf && !isVideo}
                                >
                                  <Visibility />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={handleRemoveFile}
                                >
                                  <Delete />
                                </IconButton>
                              </>
                            ),
                          }}
                          error={
                            isSubmitted && (!selectedFile || fileSizeError)
                          }
                        />
                        {isSubmitted && !selectedFile && (
                          <FormHelperText error>
                            Please select a file to upload
                          </FormHelperText>
                        )}
                        {fileSizeError && (
                          <FormHelperText error>
                            File size exceeds the maximum limit of 10MB
                          </FormHelperText>
                        )}
                        {videoDurationError && (
                          <FormHelperText error>
                            Video duration exceeds the maximum limit of 60
                            seconds
                          </FormHelperText>
                        )}
                      </Grid2>
                      <Grid2 size={{ xs: 3, sm: 3, md: 3, lg: 1, xl: 1 }}>
                        <Tooltip title="Browse" arrow>
                          <IconButton
                            color="secondary"
                            component="label"
                            size="medium"
                            fullWidth
                          >
                            <CloudUpload />
                            <input
                              type="file"
                              accept="application/pdf,image/*,video/*"
                              hidden
                              onChange={handleFileChange}
                            />
                          </IconButton>
                        </Tooltip>
                      </Grid2>

                      {selectedFile && (
                        <Grid2 size={12}>
                          <Typography
                            variant="caption"
                            color={fileSizeError ? "error" : "text.secondary"}
                          >
                          </Typography>
                        </Grid2>
                      )}
                    </Grid2> */}
                  </Grid2>

                  <Grid2
                    size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                    spacing={1}
                    sx={{ mt: 1 }}
                  >
                    <GenericTextField
                      label="Description"
                      multiline
                      rows={4}
                      value={description}
                      onChange={handleDescriptionChange}
                      placeholder="Enter any additional description here..."
                      inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
                      helperText={`${description.length}/${MAX_DESCRIPTION_LENGTH}`}
                      />
                    {/* <TextField
                      label="Description"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={4}
                      value={description}
                      onChange={handleDescriptionChange}
                      placeholder="Enter any additional description here..."
                      inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
                      helperText={`${description.length}/${MAX_DESCRIPTION_LENGTH}`}
                    /> */}
                  </Grid2>
                </Grid2>
              </Grid2>
            </Card>
          </Grid2>
        </Grid2>
      </Grid2>

      {/* Preview Dialog */}
      <Dialog
        open={openPreview}
        onClose={handlePreviewClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          File Preview
          <IconButton
            onClick={handlePreviewClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {isImage ? (
            <Grid2 size={12} container justifyContent="center">
              <img
                src={previewUrl}
                alt="Preview"
                style={{ maxWidth: "100%", maxHeight: "70vh" }}
              />
            </Grid2>
          ) : isPdf ? (
            <Grid2 size={12} sx={{ height: "70vh" }}>
              <iframe
                src={previewUrl}
                width="100%"
                height="100%"
                title="PDF Preview"
              />
            </Grid2>
          ) : isVideo ? (
            <Grid2 size={12} container justifyContent="center">
            <video
      src={previewUrl}
      controls
      muted={false}
      preload="metadata"
      playsInline={true}                    // Add this
      webkit-playsinline="true"             // Add this
      controlsList="nodownload"
      style={{
        maxWidth: "100%",
        maxHeight: "70vh",
        outline: "none",
      }}
      onLoadedMetadata={(e) => {
        // Ensure audio is enabled and video is properly configured for mobile
        e.target.muted = false;
        e.target.volume = 0.8;
        e.target.playsInline = true;        // Ensure it's set
        e.target.setAttribute("webkit-playsinline", "true"); // For older iOS
      }}
      onError={(e) => {
        console.error("Video preview error:", e);
        showSnackbar("Error loading video preview. Video format may not be supported on this device.", "error");
      }}
      onCanPlay={(e) => {
        // Additional mobile optimization
        console.log("Video can play");
      }}
    >
      Your browser does not support the video tag.
    </video>
            </Grid2>
          ) : (
            <Typography>Preview not available for this file type</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Grid2>
  );
}

export default MedicalRecord;